import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Alert, Form } from "react-bootstrap";
import EventDetail from "../../events/interfaces/EventDetail";
import { ContactFilter, ContactType } from "../types";
import { SendMailPullRequest } from "./sendMailPullRequest";
type FormValue = string | number | undefined;
type FormOption = {
  label: string;
  value: FormValue;
  name?: string;
  inline?: boolean;
  current?: boolean;
};
type FormField = {
  readOnly?: boolean;
  label?: string;
  value?: FormValue;
  name?: string;
  placeholder?: string;
  defaultValue?: FormValue;
  required?: boolean;
  type:
    | "hidden"
    | "text"
    | "select"
    | "checkbox"
    | "checkboxGroup"
    | "radio"
    | "radioGroup"
    | "email";
  size?: "sm" | "lg";
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  options?: FormOption[];
};

const ContactList = ({ event }: { event: EventDetail }) => {
  const reportForm = useRef<HTMLFormElement>(null);
  const [contactType, setContactType] = useState<ContactType>("pastEvent");
  const [conditionalFields, setConditionalFields] = useState<FormField[]>([]);
  const [filters, setFilters] = useState<ContactFilter[]>([]);
  const [email, setEmail] = useState<string>("");
  const [numberOfPriorEvents, setNumberOfPriorEvents] = useState<number>(1);
  const [orgType, setOrgLevel] = useState<string>("chapter");
  const [zip, setZip] = useState<string>("");
  const numPriorEventFields: () => FormField = useCallback(
    () => ({
      label: "Number of Prior Years",
      defaultValue: numberOfPriorEvents,
      required: true,
      type: "select",
      onChange: (evt: ChangeEvent<any>) => {
        setNumberOfPriorEvents(evt.target.value);
      },
      options: [
        {
          label: "1",
          value: 1,
        },
        {
          label: "2",
          value: 2,
        },
        {
          label: "3",
          value: 3,
        },
        {
          label: "4",
          value: 4,
        },
        {
          label: "5",
          value: 5,
        },
      ],
    }),
    [numberOfPriorEvents]
  );
  const eventIdField: () => FormField = useCallback(
    () => ({
      label: "Event ID",
      value: event.EventLookupId,
      required: true,
      name: "eventId",
      type: "text",
      readOnly: true,
    }),
    [event.EventLookupId]
  );
  const zipField: () => FormField = useCallback(
    () => ({
      label: "Zip Code",
      value: zip,
      required: true,
      name: "zip",
      type: "text",
      onChange: (evt: ChangeEvent<HTMLInputElement>) => {
        setZip(evt.target.value);
      },
    }),
    [zip]
  );
  const emailField: () => FormField = useCallback(
    () => ({
      label: "Email",
      value: email,
      required: true,
      name: "email",
      type: "email",
      onChange: (evt: ChangeEvent<HTMLInputElement>) => {
        setEmail(evt.target.value);
      },
    }),
    [email]
  );
  const filterFields: () => FormField = useCallback(
    () => ({
      label: "Filters",
      type: "checkboxGroup",
      onChange: (evt: ChangeEvent<HTMLInputElement>) => {
        const adding = evt.target.checked;
        const value = evt.target.value;
        const newState = adding
          ? [...filters, value as ContactFilter]
          : filters.filter((f) => f !== value);
        setFilters(newState);
      },
      options: [
        {
          label: "Include children (under 18)",
          value: "includeChildren",
          inline: true,
          current: !!filters.filter((f) => f === "includeChildren").length,
        },
        {
          label: "Include only Sponsor level contacts",
          value: "sponsorsOnly",
          inline: true,
          current: !!filters.filter((f) => f === "sponsorsOnly").length,
        },
      ],
    }),
    [filters]
  );

  const volunteersFilterFields: () => FormField = useCallback(
    () => ({
      label: "Filters",
      type: "checkboxGroup",
      onChange: (evt: ChangeEvent<HTMLInputElement>) => {
        const adding = evt.target.checked;
        const value = evt.target.value;
        const newState = adding
          ? [...filters, value as ContactFilter]
          : filters.filter((f) => f !== value);
        setFilters(newState);
      },
      options: [
        {
          label: "Include children (under 18)",
          value: "includeChildren",
          inline: true,
          current: !!filters.filter((f) => f === "includeChildren").length,
        },
      ],
    }),
    [filters]
  );

  const chapterIdField: () => FormField = useCallback(
    () => ({
      label: "Chapter ID",
      value: event.ChapterId,
      name: "chapterId",
      required: true,
      type: "text",
      readOnly: true,
    }),
    [event.ChapterId]
  );
  const orgTypeField: () => FormField = useCallback(
    () => ({
      label: "Organization Level",
      defaultValue: orgType,
      required: true,
      type: "select",
      onChange: (evt: ChangeEvent<any>) => {
        setOrgLevel(evt.target.value);
      },
      options: [
        {
          label: "Chapter",
          value: "chapter",
        },
        {
          label: "Zone",
          value: "zone",
        },
        {
          label: "District",
          value: "district",
        },
        {
          label: "State",
          value: "state",
        },
        {
          label: "RD",
          value: "regional director",
        },
      ],
    }),
    [orgType]
  );

  useEffect(() => {
    switch (contactType) {
      case "pastEvent":
        setConditionalFields([
          eventIdField(),
          numPriorEventFields(),
          filterFields(),
        ]);
        break;
      case "chapter":
        setConditionalFields([
          chapterIdField(),
          numPriorEventFields(),
          filterFields(),
        ]);
        break;
      case "volunteers":
        setConditionalFields([
          chapterIdField(),
          orgTypeField(),
          volunteersFilterFields(),
        ]);
        break;
      case "constituents":
        setConditionalFields([
          chapterIdField(),
          numPriorEventFields(),
          orgTypeField(),
          filterFields(),
        ]);
        break;
      case "zip":
        setConditionalFields([
          zipField(),
          numPriorEventFields(),
          filterFields(),
        ]);
        break;
      case "currentEvent":
        setConditionalFields([]);
        break;
    }
  }, [
    contactType,
    chapterIdField,
    eventIdField,
    filterFields,
    numPriorEventFields,
    orgTypeField,
    zipField,
    event.ChapterId,
    event.EventLookupId,
    filters,
    numberOfPriorEvents,
    orgType,
    volunteersFilterFields,
  ]);

  const fields: FormField[] = [
    {
      label: "Find contacts from",
      size: "lg",
      placeholder: "",
      required: false,
      type: "radioGroup",
      options: [
        {
          label: "Past attendees of this event",
          value: "pastEvent",
          name: "contactType",
          current: contactType === "pastEvent",
        },
        {
          label: "Past attendees of this chapter",
          value: "chapter",
          name: "contactType",
          current: contactType === "chapter",
        },
        {
          label: "Active volunteers of this chapter",
          value: "volunteers",
          name: "contactType",
          current: contactType === "volunteers",
        },
        {
          label: "General constituents of this chapter or higher chapter level",
          value: "constituents",
          name: "contactType",
          current: contactType === "constituents",
        },
        {
          label: "Constituents for a zip code (5 digits)",
          value: "zip",
          name: "contactType",
          current: contactType === "zip",
        },
      ],
      onChange: (evt: ChangeEvent<HTMLInputElement>) =>
        setContactType(evt.target.value as ContactType),
    },
    emailField(),
    ...conditionalFields,
  ];

  const getStatusMessage = () => {
    let contactTypeMsg = "";
    let filterMsg = "";

    if (contactType === "chapter") {
      contactTypeMsg = `Find all past attendees of this chapter's events for the past ${numberOfPriorEvents} years they were held.`;
    }
    if (contactType === "pastEvent") {
      contactTypeMsg = `Find all past attendees of this event for the past ${numberOfPriorEvents} times it was held.`;
    }

    if (contactType === "volunteers") {
      contactTypeMsg = `Can be used to show active volunteers for any group constituent.`;
    }
    if (contactType === "constituents") {
      contactTypeMsg = `Find all constituents in this ${orgType}.`;
    }
    if (contactType === "zip") {
      contactTypeMsg = `Find all constituents for this zip code in the prior 1 year.`;
    }
    if (contactType === "currentEvent") {
      contactTypeMsg = `Find all current attendees of this event (ticket buyers and guests).`;
    }
    if (contactType !== "currentEvent") {
      filterMsg = filters.reduce((acc, f) => {
        switch (f) {
          case "includeChildren":
            acc += "Including children (under 18). ";
            break;
          case "sponsorsOnly":
            acc += "Including Sponsor level contacts only. ";
        }

        return acc;
      }, "");
    }
    return `${contactTypeMsg} ${filterMsg}`;
  };

  const validate = () => {
    return reportForm?.current?.reportValidity
      ? reportForm.current.reportValidity()
      : false;
  };

  const formId = `event-${event.EventId}`;
  return (
    <>
      <h1>Create a New Contact List</h1>
      <Form ref={reportForm}>
        {fields.map((f, i) => {
          if (f.type === "select") {
            return (
              <Form.Group key={`${formId}-${i}`} controlId={`${f.label}-${i}`}>
                <Form.Label>{f.label}</Form.Label>
                <Form.Control
                  as="select"
                  value={f.value}
                  defaultValue={f.defaultValue}
                  name={f.name}
                  size={f.size}
                  onChange={f.onChange}
                >
                  {f.options?.map((o, j) => (
                    <option key={`${o.value}-${j}`} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            );
          }
          if (f.type === "checkboxGroup") {
            return (
              <Form.Group key={`${formId}-${i}`} controlId={`${f.label}-${i}`}>
                <Form.Label>{f.label}</Form.Label>
                <div>
                  {f.options?.map((o, j) => (
                    <Form.Check
                      checked={o.current}
                      inline={o.inline}
                      key={`${o.value}-${j}`}
                      type="checkbox"
                      id={`${o.name}-${j}`}
                      name={o.name}
                      value={o.value}
                      label={o.label}
                      onChange={f.onChange}
                    />
                  ))}
                </div>
              </Form.Group>
            );
          }
          if (f.type === "radioGroup") {
            return (
              <Form.Group key={`${formId}-${i}`} controlId={`${f.label}-${i}`}>
                <Form.Label className="me-3">{f.label}</Form.Label>
                <div>
                  {f.options?.map((o, j) => (
                    <Form.Check
                      checked={o.current}
                      inline={o.inline}
                      key={`${o.value}-${j}`}
                      type="radio"
                      id={`${o.name}-${j}`}
                      name={o.name}
                      value={o.value}
                      label={o.label}
                      onChange={f.onChange}
                    />
                  ))}
                </div>
              </Form.Group>
            );
          }
          return (
            <Form.Group key={`${formId}-${i}`} className="mt-2">
              <Form.Label>{f.label}</Form.Label>
              <Form.Control
                readOnly={f.readOnly}
                required={f.required}
                onChange={f.onChange}
                placeholder={f.placeholder}
                value={f.value}
                defaultValue={f.defaultValue}
                type={f.type}
              />
            </Form.Group>
          );
        })}
        <Alert variant="info">{getStatusMessage()}</Alert>
        <SendMailPullRequest
          contactType={contactType}
          conditionalFields={conditionalFields}
          email={email}
          filters={filters}
          numberOfPriorEvents={numberOfPriorEvents}
          orgType={orgType}
          zip={zip}
          id={
            contactType === "chapter" ||
            contactType === "constituents" ||
            contactType === "volunteers"
              ? event.ChapterId
              : event.EventLookupId
          }
          eventId={event.EventLookupId}
          validate={validate}
        ></SendMailPullRequest>
      </Form>
    </>
  );
};

export default ContactList;
